import { useState, useEffect } from 'react';
import { TealiumUTag } from 'shared/types/tealium/TealiumUTag';
import DownPaymentRequest from '@/components/customercenter-ui/components/modals/customerResult/downPaymentRequest';
import { triggerTealiumEvent, getPageUtagData } from '@/components/customercenter-ui/utils/tealiumUtils';
import { TranslateText } from '@/helpers/utils/siteUtils';
import { DEFAULT_SITE_LANG, CANCEL_TYPE, ORDER_CONFIRMATION_NUMBER } from '../../../../../../constants/constant';
import { CANCEL_ORDER_EVENT_TYPES } from '../../../cart/constants';
import AdjustOrderModal from '../../../modals/orderDetails/adjustOrder';
import CancelOrderModal from '../../../modals/orderDetails/cancelOrder';
import OrderTrackLogModal from '../../../modals/orderDetails/orderTrackLog';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { PageTexts } from '../../i18n';
import { OrderDetailsType, OrderSummaryInfo } from '../../types';
import { getOrderDetailsWithFields } from '../../util';

const OrderDetails = ({
  orderDetails,
  isOrderShipped,
  customerId,
  addNewNotes,
  showOrderAdjustment,
  isOrderCancelable,
  getUpdatedData,
  EstimatedTotal,
  order,
  customerDetails,
  loading,
  isSoldAccount,
  showActiveBrandSection = true,
}: {
  orderDetails: OrderDetailsType;
  isOrderShipped: boolean;
  customerId: string;
  addNewNotes: (note: string) => void;
  showOrderAdjustment: boolean;
  isOrderCancelable: boolean;
  EstimatedTotal: OrderSummaryInfo;
  getUpdatedData: () => void;
  order?: any;
  customerDetails: any;
  loading: any;
  isSoldAccount: boolean;
  showActiveBrandSection?: boolean;
}) => {
  const { accountData, accountAddress } = getOrderDetailsWithFields(orderDetails);
  const [adjustOrderModal, setOrderAdjustModal] = useState(false);
  const [downPaymentRequestOpen, setDownPaymentRequestOpen] = useState(false);
  const [showDPButton, setShowDPButton] = useState(true);

  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);

  useEffect(() => {
    const pageLinkUtagData: TealiumUTag = getPageUtagData('Order Details');
    triggerTealiumEvent(pageLinkUtagData);
  }, []);

  const handleDownpaymentSuccess = (success: boolean) => {
    if (success) {
      setDownPaymentRequestOpen(false);
      setShowDPButton(false);
    }
  };

  return (
    <Card className="w-full rounded-md border-foggyGray shadow-lg">
      <CardHeader className="p-5">
        <CardTitle className="flex flex-wrap">
          <p className="mr-auto text-lg font-bold">{Texts.orderDetailsSubHeading}</p>
          <section className="flex flex-wrap gap-2">
            {showOrderAdjustment && showActiveBrandSection ? (
              <button
                onClick={() => setOrderAdjustModal(true)}
                className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
              >
                {Texts.adjustOrderText}
              </button>
            ) : (
              <></>
            )}
            {adjustOrderModal && (
              <AdjustOrderModal
                Texts={Texts}
                open={adjustOrderModal}
                setOpen={setOrderAdjustModal}
                isOrderShipped={isOrderShipped}
                addNewNotes={addNewNotes}
                getUpdatedData={getUpdatedData}
                EstimatedTotal={EstimatedTotal}
              ></AdjustOrderModal>
            )}
            {order?.PostSubmissionInfo?.AvailableActions?.canMakeDownPayment && showDPButton && (
              <DownPaymentRequest
                open={downPaymentRequestOpen}
                setOpen={setDownPaymentRequestOpen}
                order={order}
                handleDownpaymentSuccess={handleDownpaymentSuccess}
                isOrderDetails={true}
                customerDetails={customerDetails}
                loading={loading}
                isSoldAccount={isSoldAccount}
              ></DownPaymentRequest>
            )}
            <OrderTrackLogModal Texts={Texts}></OrderTrackLogModal>
            {isOrderCancelable && showActiveBrandSection && (
              <CancelOrderModal
                Texts={Texts}
                cancelType={CANCEL_TYPE.ORDER}
                CancelEvent={CANCEL_ORDER_EVENT_TYPES.ENTIRE_ORDER}
                comercetoolsOrder={false}
                showOptions
                className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
                customerId={customerId}
                getUpdatedData={getUpdatedData}
              ></CancelOrderModal>
            )}
          </section>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid w-4/5 grid-cols-2 gap-4 text-sm font-normal">
          <div>
            {accountData?.map((items) => (
              <div className="flex gap-2" key={`key-${items.field}`}>
                <div className="mb-3 flex-1">{items.field}</div>
                <div className={`flex-1 font-semibold ${items?.mask ? 'gbmask' : ''}`}>{items?.value}</div>
              </div>
            ))}
          </div>
          <div>
            {accountAddress?.map((items) => (
              <div className="flex gap-2" key={`key-${items.field}`}>
                <p className="mb-3 flex-1 font-normal">{items.field}</p>
                {items?.key !== ORDER_CONFIRMATION_NUMBER ? (
                  <p className={`flex-1 font-semibold ${items?.mask ? 'gbmask' : ''}`}>{items.value}</p>
                ) : (
                  <section className={'flex-1 font-semibold'}>
                    {items?.value ? (
                      <section className="flex">
                        <span>{items?.value?.slice(0, 2)}</span>{' '}
                        <span className="gbmask">{items?.value?.slice(2)}</span>
                      </section>
                    ) : (
                      <></>
                    )}
                  </section>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderDetails;
